import type {
  ArticleListItem as RelatedArticleType,
  Image as ImageType
} from 'types/capi/shared';

import ChevronIcon from '~/assets/icons/Chevron';
import Image from '~/components/Image';
import Tracked from '~/components/Tracked/index';
import useRegion from '~/hooks/useRegion';

type RecommendedArticlesProps = {
  articles: RelatedArticleType[];
  heading?: string;
  viewMoreNewsPath?: string;
};

type ArticleCardProps = {
  excerpt: string;
  featuredImage: ImageType;
  path: string;
  title: string;
};

function ArticleCard({
  title,
  featuredImage,
  path,
  excerpt
}: ArticleCardProps) {
  return (
    <div className="flex items-stretch justify-around">
      <Tracked.InternalLink
        variant="none"
        name="RELATED_ARTICLES"
        pagePosition="Body"
        to={path}
        className="cursor-pointer rounded-lg border border-grey-midnight-read"
      >
        <Image
          className="rounded-t-lg"
          alt={featuredImage?.alt}
          src={featuredImage?.url}
          pad={false}
          quality={100}
          loading="lazy"
          responsiveOptions={{
            md: { height: 450, width: 900 },
            xs: { height: 300, width: 672 }
          }}
        />

        <div className="flex flex-col gap-6 p-8">
          <p className="text-2xl font-bold">{title}</p>
          <p>{excerpt}</p>
          <div className="flex items-center gap-2">
            <p>View article</p>
            <div className="rotate-270">
              <ChevronIcon stroke="black" width="10" height="10" />
            </div>
          </div>
        </div>
      </Tracked.InternalLink>
    </div>
  );
}

export default function RecommendedArticles({
  heading = 'Related articles',
  articles,
  viewMoreNewsPath
}: RecommendedArticlesProps) {
  const region = useRegion();

  if (articles.length === 0 || region.code !== 'GB') return null;

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-8">
      <div className="flex items-center justify-between pt-8 lg:pt-12">
        <h3 className="text-lg font-bold">{heading}</h3>
        <Tracked.ExternalLink
          variant="none"
          name="RELATED_ARTICLES"
          pagePosition="Body"
          href={viewMoreNewsPath ? viewMoreNewsPath : '/news/'}
          className="text-sm underline hover:text-gray-600"
          data-page_position="Body"
        >
          View more news
        </Tracked.ExternalLink>
      </div>
      <div className="flex flex-col items-center justify-center py-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {articles.map((article) => {
            return (
              <ArticleCard
                key={article.title}
                title={article.title}
                featuredImage={article.featured_image}
                path={article.path}
                excerpt={article.excerpt}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
